import React, { ChangeEvent } from 'react'
import { Controller } from 'react-hook-form'
import { EyeIcon, ExclamationCircleIcon } from '../icons'
import { Box, Button, Input, Select, Text, Stack, Checkbox } from '../../vanilla'
import { useToggle } from '../../hooks/use-toggle'
import { Textarea } from '../../vanilla'
import { RadioGroup } from './partials/radio-group'
import { Field as FieldType } from '../../types/forms'


// Helper to map field names to data-test-selectors
const getErrorSelector = (name: string): string | undefined => {
  switch (name) {
    case 'email':
      return 'email-error'
    case 'password':
      return 'password-error'
    case 'phone':
      return 'phone-error'
    case 'firstName':
      return 'first-name-error'
    case 'lastName':
      return 'last-name-error'
    case 'searchForAddress':
      return 'postal-code-error'
    default:
      return undefined
  }
}

export const Field = ({
  name,
  label = '',
  showAsterisk,
  type = 'text',
  options = [],
  rules = {},
  error,
  placeholder,
  inputProps,
  inputGroupProps,
  control,
  defaultValue,
  helpText,
  children,
  errorTextProps = {},
  onSubmitValidation = false,
  disabled = false,
  hideError = false,
  isSelfServe = false
}: FieldType) => {
  const [hidePassword, toggleHidePassword] = useToggle(true)

  const inputType =
    type === 'password' && hidePassword ? 'password' : type === 'password' ? 'text' : type

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      defaultValue={defaultValue}
      render={(renderProps) => {
        const {
          field: { onChange, value, ref, onBlur },
          fieldState: { invalid, isTouched },
          formState: { errors, isSubmitted }
        } = renderProps

        const _inputProps =
          typeof inputProps === 'function' ? inputProps({ value, onChange, onBlur }) : inputProps

        return (
          <Stack spacing="8px" width="full" role="group">
            {type === 'radioGroup' ? <>
              <RadioGroup defaultValue={defaultValue as string} name={name} options={options} onChange={onChange} />
            </> : (
              <Box position="relative" {...inputGroupProps}>
                {['text', 'password', 'email', 'phone', 'tel', 'number'].includes(type) && (
                  <Input
                    ref={ref}
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                    label={label}
                    showAsterisk={showAsterisk}
                    name={name}
                    type={inputType}
                    placeholder={placeholder}
                    disabled={disabled}
                    hasError={error != null || ((type === 'email' || type === 'password') && !!errors.global)}
                    // On login, only validate the fields on submit
                    validatedSuccessfully={
                      onSubmitValidation
                        ? isSubmitted && !invalid && isTouched && !errors.global
                        : !invalid && isTouched && !errors.global
                    }
                    {..._inputProps}
                  />
                )}

                {type === 'hidden' && (
                  <Input
                    ref={ref}
                    onChange={onChange}
                    value={value}
                    type="hidden"
                    {..._inputProps}
                    validatedSuccessfully={!invalid && isTouched}
                  />
                )}

                {type === 'password' && (
                  <Box
                    position="absolute"
                    right="0px"
                    style={{ top: '50%', transform: 'translateY(-50%)' }}
                  >
                    <Button
                      height="36px"
                      type="button"
                      variant="unstyled"
                      size="icon"
                      aria-label="Show password"
                      onClick={(e: React.MouseEvent | React.TouchEvent) => {
                        e.preventDefault()
                        toggleHidePassword()
                      }}
                    >
                      <EyeIcon boxSize="12px" color="black" opacity={hidePassword ? '0.3' : '1'} />
                    </Button>
                  </Box>
                )}

                {type === 'select' && (
                  // @ts-ignore
                  <Select<'select'>
                    name={name}
                    ref={ref}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                      onChange(e.target.value)
                    }}
                    value={value}
                    label={label}
                    placeholder={placeholder}
                    hasError={error != null}
                    validatedSuccessfully={
                      isSelfServe
                        ? !invalid && value !== defaultValue
                        : !invalid && isTouched
                    }
                    onBlur={onBlur}
                    {..._inputProps}
                  >
                    {options.map((opt) => (
                      <option key={`${opt.label}-${opt.value}`} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                )}

                {type === 'checkbox' && (
                  <Box display="flex" flexDirection="row">
                    <Checkbox
                      name={name}
                      checked={value}
                      label={label}
                      hasError={error != null}
                      showAsterisk={showAsterisk}
                      {...(_inputProps || {})}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
                    />
                  </Box>
                )}

                {type === 'textarea' && (
                  // @ts-ignore
                  <Textarea
                    label={label}
                    ref={ref}
                    name={name}
                    onChange={onChange}
                    value={value}
                    type="hidden"
                    onBlur={onBlur}
                    {..._inputProps}
                    placeholder={placeholder}
                    validatedSuccessfully={!invalid && isTouched}
                  />
                )}
                {children}
              </Box>
            )}

            {error && type !== 'hidden' && !hideError && (
              <Text
                as="span"
                variant="text4"
                color="error"
                display="inline-block"
                marginLeft="8px"
                lineHeight="shorter"
                data-test-selector={getErrorSelector(name)}
                {...errorTextProps}
              >
                {!(error.type && error.type === "custom.noExclamation") && (
                  <ExclamationCircleIcon
                    color="error"
                    display="inline-block"
                    boxSize="12px"
                    style={{ marginRight: '6.5px' }}
                  />
                )}
                {error.message}
              </Text>
            )}
            {helpText}
          </Stack>
        )
      }}
    />
  )
}
